import { useEffect, useState, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { get } from '../services/ApiService';

interface CepData {
    cep: string;
    endereco: string;
    bairro: string;
    cidade: string;
    estado: string;
    complemento?: string;
    localizacao?: string;
    cidadeObj?: {
        id: number;
        nome: string;
        estado: string;
    };
}

interface UseCepAutocompleteOptions {
    cepField: string;
    enderecoField: string;
    bairroField: string;
    numeroField?: string; // Campo para focar após buscar
    cidadeField?: string; // Pode ser string (nome da cidade) ou objeto (suggest)
    estadoField?: string;
    complementoField?: string;
    localizacaoField?: string; // Campo de localização (lat, long)
    /**
     * Suspende a consulta enquanto o formulário preenche o endereço por conta
     * própria (importado de outro cadastro). Sem isso, o setValue no CEP dispara a
     * busca e os Correios sobrescrevem o endereço que acabou de ser importado.
     */
    pausado?: boolean;
    onSuccess?: (data: CepData) => void;
    onError?: (error: any) => void;
    onLoadingChange?: (loading: boolean) => void;
}

export const useCepAutocomplete = (options: UseCepAutocompleteOptions) => {
    const { watch, setValue, setFocus } = useFormContext();
    const cepValue = watch(options.cepField);
    const enderecoValue = watch(options.enderecoField);
    const bairroValue = watch(options.bairroField);
    const cidadeValue = options.cidadeField ? watch(options.cidadeField) : '';
    const localizacaoValue = options.localizacaoField ? watch(options.localizacaoField) : '';
    const [isLoading, setIsLoading] = useState(false);

    // Ref para rastrear o CEP anterior
    const previousCepRef = useRef<string>('');
    // Ref para saber se é a primeira renderização
    const isInitialMountRef = useRef(true);

    useEffect(() => {
        const buscarCep = async () => {
            if (!cepValue) return;

            // Remove caracteres não numéricos
            const cepLimpo = cepValue.replace(/[^0-9]/g, '');

            // Só busca quando CEP estiver completo (8 dígitos)
            if (cepLimpo.length !== 8) return;

            // Se é a primeira renderização E os campos já estão preenchidos, não busca
            // (evita busca desnecessária ao abrir form de edição)
            if (isInitialMountRef.current && enderecoValue && bairroValue && cidadeValue) {
                previousCepRef.current = cepLimpo;
                isInitialMountRef.current = false;
                return;
            }

            // Se o CEP não mudou, não busca
            if (previousCepRef.current === cepLimpo) {
                return;
            }

            // Pausado: registra o CEP como visto e não busca, para o endereço
            // importado de outro cadastro não ser sobrescrito pelos Correios.
            if (options.pausado) {
                previousCepRef.current = cepLimpo;
                isInitialMountRef.current = false;
                return;
            }

            // Atualiza o CEP anterior
            previousCepRef.current = cepLimpo;
            isInitialMountRef.current = false;

            // Marca o início para calcular tempo de loading
            const startTime = Date.now();

            try {
                setIsLoading(true);
                if (options.onLoadingChange) {
                    options.onLoadingChange(true);
                }

                // Monta a URL com parâmetros, incluindo localização atual se existir
                let url = `/?route=util&action=buscarCep&cep=${cepLimpo}`;
                if (localizacaoValue) {
                    url += `&localizacao=${encodeURIComponent(localizacaoValue)}`;
                }

                const response = await get(url);

                if (response.data.success && response.data.data) {
                    const data: CepData = response.data.data;

                    // Preenche endereço (sem bloquear)
                    if (data.endereco) {
                        setValue(options.enderecoField, data.endereco);
                    }

                    // Preenche bairro (sem bloquear)
                    if (data.bairro) {
                        setValue(options.bairroField, data.bairro);
                    }

                    // Preenche cidade com objeto completo (para suggest)
                    if (options.cidadeField && data.cidade) {
                        setValue(options.cidadeField, data.cidade);
                    }

                    // Preenche estado (sem bloquear)
                    if (options.estadoField && data.estado) {
                        setValue(options.estadoField, data.estado);
                    }

                    // Preenche complemento (sem bloquear)
                    if (options.complementoField && data.complemento) {
                        setValue(options.complementoField, data.complemento);
                    }

                    // Preenche localização (coordenadas) se o backend retornou
                    if (options.localizacaoField && data.localizacao) {
                        setValue(options.localizacaoField, data.localizacao);
                    }

                    // Decide qual campo focar: se endereço veio vazio, foca no endereço, senão foca no número
                    setTimeout(() => {
                        if (!data.endereco) {
                            // Se endereço veio vazio, foca no campo de endereço
                            setFocus(options.enderecoField);
                        } else if (options.numeroField) {
                            // Se endereço preenchido, foca no campo número
                            setFocus(options.numeroField);
                        }
                    }, 100);

                    // Callback de sucesso
                    if (options.onSuccess) {
                        options.onSuccess(data);
                    }
                }
            } catch (error) {
                console.error('Erro ao buscar CEP:', error);
                if (options.onError) {
                    options.onError(error);
                }
            } finally {
                // Garante que o loading fique visível por pelo menos 800ms
                const elapsedTime = Date.now() - startTime;
                const minLoadingTime = 800;
                const remainingTime = Math.max(0, minLoadingTime - elapsedTime);

                if (remainingTime > 0) {
                    await new Promise(resolve => setTimeout(resolve, remainingTime));
                }

                setIsLoading(false);
                if (options.onLoadingChange) {
                    options.onLoadingChange(false);
                }
            }
        };

        buscarCep();
    }, [cepValue, localizacaoValue]);

    return null;
};
